import { useEffect, useRef, useState } from "react";
import { useOnline } from "@/hooks/useOnline";
import { unsentCount, useTaskStore } from "@/stores/taskStore";
import { changeCount, pluralRu } from "@/lib/plural";
import { outboxOpen, onOpRejected } from "@/lib/syncEngine";

// An online push normally lands well before this; only a stuck queue is worth a banner.
export const QUEUE_SHOWN_AFTER_MS = 2_000;
export const SENT_SHOWN_MS = 2_000;

const WAIT_FORMS = ["ждёт", "ждут", "ждут"] as const;

function useOverdue(waiting: boolean): boolean {
  const [overdue, setOverdue] = useState(false);
  useEffect(() => {
    setOverdue(false);
    if (!waiting) return;
    const timer = setTimeout(() => setOverdue(true), QUEUE_SHOWN_AFTER_MS);
    return () => clearTimeout(timer);
  }, [waiting]);
  return overdue;
}

export default function ConnectionBanner() {
  const online = useOnline();
  const waiting = useTaskStore(unsentCount);
  // Re-renders when the local copy becomes whole, which is when the outbox opens.
  const ready = useTaskStore((s) => s.ready);
  const overdue = useOverdue(waiting > 0);
  const [queueShown, setQueueShown] = useState(false);
  const showQueue = waiting > 0 && (!online || overdue || queueShown);
  const [sent, setSent] = useState(false);
  const refused = useRef(false);

  useEffect(
    () =>
      onOpRejected(() => {
        refused.current = true;
      }),
    [],
  );

  useEffect(() => {
    if (showQueue && !queueShown) {
      setQueueShown(true);
      setSent(false);
      refused.current = false;
    } else if (waiting === 0 && queueShown) {
      setQueueShown(false);
      setSent(!refused.current);
    }
  }, [showQueue, queueShown, waiting]);

  useEffect(() => {
    if (!sent) return;
    const timer = setTimeout(() => setSent(false), SENT_SHOWN_MS);
    return () => clearTimeout(timer);
  }, [sent]);

  let text: string | null = null;
  if (showQueue) text = `${changeCount(waiting)} ${pluralRu(waiting, WAIT_FORMS)} отправки`;
  else if (!online) text = ready && outboxOpen() ? "Нет сети — изменения сохранятся на устройстве" : "Нет сети";
  else if (sent) text = "Отправлено";
  if (!text) return null;

  return (
    <div role="status" className="connection-banner">
      {text}
    </div>
  );
}
